/**
 * Convert persisted ConversationMessage to UIMessage format.
 *
 * Shared by persisted chat implementations that need to hydrate UI messages.
 *
 * @module hooks/chat/convertPersistedToUIMessage
 */

import type { UIMessage } from '@ai-sdk/react';
import type { ConversationMessage } from '../useConversations';

/**
 * Convert a persisted ConversationMessage into a UIMessage.
 * Accepts an optional metadataMapper for feature-specific metadata
 * (e.g., flow builder adds originalRole and createdAt).
 */
export function convertPersistedToUIMessage<TMessage extends UIMessage = UIMessage>(
  msg: ConversationMessage,
  metadataMapper?: (msg: ConversationMessage) => TMessage['metadata']
): TMessage {
  // If parts are stored and non-empty, use them directly
  if (msg.parts && Array.isArray(msg.parts) && msg.parts.length > 0) {
    return {
      id: msg.id,
      role: msg.role as TMessage['role'],
      parts: msg.parts as TMessage['parts'],
      ...(metadataMapper ? { metadata: metadataMapper(msg) } : {}),
    } as TMessage;
  }

  // Otherwise, build parts from content and tool info
  const parts: TMessage['parts'] = [];

  if (msg.content) {
    parts.push({ type: 'text', text: msg.content });
  }

  if (msg.toolName && msg.toolCallId) {
    parts.push({
      type: `tool-${msg.toolName}` as 'text',
      toolName: msg.toolName,
      toolCallId: msg.toolCallId,
      state: msg.toolOutput ? 'output-available' : 'pending',
      input: msg.toolInput ?? undefined,
      output: msg.toolOutput ?? undefined,
    } as unknown as TMessage['parts'][number]);
  }

  // Ensure at least one part exists
  if (parts.length === 0) {
    parts.push({ type: 'text', text: '' });
  }

  return {
    id: msg.id,
    role: msg.role as TMessage['role'],
    parts,
    ...(metadataMapper ? { metadata: metadataMapper(msg) } : {}),
  } as TMessage;
}
